import { DealLensStatus, normalizeDealLensStatus } from "./dealsConstants";
import { canEditSentOffer, resolveDealCollectionId } from "./dealOfferThreadUtils";

/** Statuses that still negotiate (Accept / Counter / Withdraw). */
const ALIVE_STATUSES = new Set([DealLensStatus.OPEN, DealLensStatus.COUNTERED]);

/** Terminal statuses that close chat (history remains readable). */
const CHAT_CLOSED_STATUSES = new Set([
  DealLensStatus.REJECTED,
  DealLensStatus.CANCELLED,
  DealLensStatus.EXPIRED,
  DealLensStatus.WITHDRAWN,
]);

/** Offer-bearing statuses used for list/header amount when nothing is alive. */
const DISPLAY_OFFER_STATUSES = new Set([
  DealLensStatus.OPEN,
  DealLensStatus.COUNTERED,
  DealLensStatus.ACCEPTED,
  DealLensStatus.REJECTED,
  DealLensStatus.CANCELLED,
  DealLensStatus.EXPIRED,
  DealLensStatus.WITHDRAWN,
]);

/**
 * @param {object | null | undefined} deal
 * @returns {object}
 */
function asDeal(deal) {
  return deal?._raw && typeof deal._raw === "object" ? deal._raw : deal;
}

/**
 * Converts collection-feed MESSAGE/OFFER rows into the deal shape used by action resolvers.
 *
 * @param {object[]} rows
 * @param {{ isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [viewerContext]
 * @returns {object[]}
 */
export function normalizeCollectionFeedDeals(rows, viewerContext = {}) {
  const viewerRole =
    viewerContext.isInvestor && !viewerContext.isDistributeRole
      ? "investor"
      : String(viewerContext.currentRole ?? "")
          .trim()
          .toLowerCase();
  const otherRole =
    viewerRole === "investor" ? "realtor" : viewerRole === "realtor" ? "investor" : "";

  return normalizeDealList(rows).map((row) => {
    const metadata = row?.metadata && typeof row.metadata === "object" ? row.metadata : {};
    const direction = String(row?.direction ?? "")
      .trim()
      .toUpperCase();
    const status = normalizeDealLensStatus(
      row?.status ??
        row?.offer_status ??
        metadata.offer_status ??
        (String(row?.type ?? "").toUpperCase() === "MESSAGE" ? DealLensStatus.MESSAGE : "")
    );
    const amount = Number(
      row?.calculation?.offer_amount ?? row?.offer_amount ?? metadata.offer_amount
    );
    const senderRole =
      String(row?.sender_role ?? row?.role ?? "")
        .trim()
        .toLowerCase() ||
      (direction === "OUTGOING" ? viewerRole : direction === "INCOMING" ? otherRole : "");
    const receiverRole =
      String(row?.receiver_role ?? "")
        .trim()
        .toLowerCase() ||
      (senderRole === "investor" ? "realtor" : senderRole === "realtor" ? "investor" : "");

    return {
      ...row,
      id: row?.deallens_id ?? metadata.deallens_id ?? row?.id ?? null,
      deallens_id: row?.deallens_id ?? metadata.deallens_id ?? null,
      property_id: row?.property_id ?? metadata.property_id ?? null,
      collection_id: row?.collection_id ?? metadata.collection_id ?? row?.thread ?? null,
      status,
      sender_role: senderRole,
      receiver_role: receiverRole,
      ...(Number.isFinite(amount) && amount > 0 ? { calculation: { offer_amount: amount } } : {}),
      metadata,
      raw: row,
    };
  });
}

/**
 * @param {object | null | undefined} deal
 * @returns {number}
 */
function insertTime(deal) {
  const raw = asDeal(deal);
  const t = new Date(raw?.insert_date ?? deal?._insertDate ?? "").getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Explicit submitted `calculation.offer_amount` only (not MAO fallback).
 *
 * @param {object | null | undefined} deal
 * @returns {number}
 */
export function resolveExplicitOfferAmount(deal) {
  const raw = asDeal(deal);
  const fromCalc = Number(raw?.calculation?.offer_amount);
  if (Number.isFinite(fromCalc) && fromCalc > 0) return fromCalc;
  return 0;
}

/**
 * @param {object[]} deals
 * @returns {object[]}
 */
function normalizeDealList(deals) {
  if (!Array.isArray(deals)) return [];
  return deals.filter((row) => row && typeof row === "object");
}

/**
 * Newest row by insert_date (collection tip).
 *
 * @param {object[]} deals
 * @returns {object | null}
 */
export function resolveCollectionTip(deals) {
  const rows = normalizeDealList(deals);
  if (rows.length === 0) return null;
  return [...rows].sort((a, b) => insertTime(a) - insertTime(b)).at(-1) ?? null;
}

/**
 * Newest OPEN/COUNTERED row with an explicit offer_amount.
 * If the collection tip is terminal/accepted (not MESSAGE / OPEN / COUNTERED),
 * prior counters are no longer alive — e.g. WITHDRAWN tip must not surface
 * "Awaiting reply" from an older COUNTERED row.
 *
 * @param {object[]} deals
 * @returns {object | null}
 */
export function resolveLastAliveOffer(deals) {
  const tip = resolveCollectionTip(deals);
  if (tip) {
    const tipStatus = normalizeDealLensStatus(asDeal(tip)?.status ?? tip?.status);
    // MESSAGE tips can follow an open counter; anything else non-alive ends negotiation.
    if (tipStatus !== DealLensStatus.MESSAGE && !ALIVE_STATUSES.has(tipStatus)) {
      return null;
    }
  }

  let rows = normalizeDealList(deals)
    .filter((row) => {
      const raw = asDeal(row);
      const status = normalizeDealLensStatus(raw?.status ?? row?.status);
      return ALIVE_STATUSES.has(status) && resolveExplicitOfferAmount(row) > 0;
    })
    .sort((a, b) => insertTime(a) - insertTime(b));
  const offerRows = rows.filter(
    (row) =>
      String(asDeal(row)?.type ?? "")
        .trim()
        .toUpperCase() === "OFFER"
  );
  if (offerRows.length > 0) rows = offerRows;
  return rows.at(-1) ?? null;
}

/**
 * Offer used for amount display: last-alive, else newest offer-bearing row (incl. MESSAGE tips
 * that still carry an inherited explicit offer_amount).
 *
 * @param {object[]} deals
 * @returns {object | null}
 */
export function resolveDisplayOffer(deals) {
  const rows = normalizeDealList(deals);
  const tip = resolveCollectionTip(rows);
  if (tip) {
    const raw = asDeal(tip);
    const tipStatus = normalizeDealLensStatus(raw?.status ?? tip?.status);
    if (tipStatus === DealLensStatus.ACCEPTED) {
      if (resolveExplicitOfferAmount(tip) > 0) return tip;
      const priced = rows
        .filter((row) => resolveExplicitOfferAmount(row) > 0)
        .sort((a, b) => insertTime(a) - insertTime(b))
        .at(-1);
      if (priced) return priced;
    }
  }

  const alive = resolveLastAliveOffer(deals);
  if (alive) return alive;

  const pricedRows = rows
    .filter((row) => {
      const raw = asDeal(row);
      const status = normalizeDealLensStatus(raw?.status ?? row?.status);
      if (status === DealLensStatus.MESSAGE) return resolveExplicitOfferAmount(row) > 0;
      return DISPLAY_OFFER_STATUSES.has(status) && resolveExplicitOfferAmount(row) > 0;
    })
    .sort((a, b) => insertTime(a) - insertTime(b));
  return pricedRows.at(-1) ?? null;
}

/**
 * True when the collection tip is a chat-closing terminal status.
 *
 * @param {object[]} deals
 * @returns {boolean}
 */
export function isCollectionChatClosed(deals) {
  const tip = resolveCollectionTip(deals);
  if (!tip) return false;
  const raw = asDeal(tip);
  const status = normalizeDealLensStatus(raw?.status ?? tip?.status);
  return CHAT_CLOSED_STATUSES.has(status);
}

/**
 * True when negotiation buttons should be hidden (accepted lock-in, closed, or no alive offer).
 * Messaging may still be allowed unless {@link isCollectionChatClosed}.
 *
 * @param {object[]} deals
 * @returns {boolean}
 */
export function isNegotiationLocked(deals) {
  return resolveLastAliveOffer(deals) == null;
}

/**
 * Collect deals/offers that share the tip's collection_id.
 *
 * @param {object | null | undefined} tipOffer - List row or raw deal
 * @param {object[]} [siblings=[]] - Property offers or thread deals
 * @returns {object[]}
 */
export function resolveCollectionDeals(tipOffer, siblings = []) {
  if (!tipOffer) return [];
  const tipDeal = asDeal(tipOffer);
  const siblingDeals = normalizeDealList(siblings)
    .map((row) => asDeal(row))
    .filter(Boolean);
  const collectionId = resolveDealCollectionId(tipDeal, siblingDeals);
  if (!collectionId) {
    return tipDeal ? [tipOffer] : [];
  }

  const matched = normalizeDealList(siblings).filter((row) => {
    const deal = asDeal(row);
    return resolveDealCollectionId(deal, siblingDeals) === collectionId;
  });
  return matched.length > 0 ? matched : [tipOffer];
}

/**
 * Format a display amount for conversation headlines.
 *
 * @param {object | null | undefined} offer
 * @returns {string}
 */
export function formatConversationOfferAmount(offer) {
  const amount = resolveExplicitOfferAmount(offer);
  if (!(amount > 0)) return "";
  return `$${Math.round(amount).toLocaleString("en-US")}`;
}

/**
 * Modal header/card deal when the hub list row lags the collection feed (e.g. ACCEPTED in chat).
 *
 * @param {object} params
 * @param {boolean} [params.chatClosed]
 * @param {object[]} [params.collectionDeals]
 * @param {object[]} [params.listScopedDeals]
 * @param {object | null} [params.selectedDeal]
 * @returns {object | null}
 */
function resolveAcceptedSummaryRow(tip, deals) {
  if (!tip) return null;
  const amount = resolveExplicitOfferAmount(tip);
  if (amount > 0) return tip;
  const priced = normalizeDealList(deals)
    .filter((row) => resolveExplicitOfferAmount(row) > 0)
    .sort((a, b) => insertTime(a) - insertTime(b))
    .at(-1);
  if (!priced) return tip;
  return {
    ...tip,
    status: DealLensStatus.ACCEPTED,
    calculation: {
      ...(tip.calculation && typeof tip.calculation === "object" ? tip.calculation : {}),
      offer_amount: resolveExplicitOfferAmount(priced),
    },
  };
}

export function resolveNegotiationSummaryDeal({
  chatClosed = false,
  collectionDeals = [],
  listScopedDeals = [],
  selectedDeal = null,
} = {}) {
  const deals = Array.isArray(collectionDeals) ? collectionDeals : [];
  const tip = resolveCollectionTip(deals);
  const tipStatus = tip
    ? normalizeDealLensStatus(tip?.status ?? tip?.metadata?.offer_status)
    : "";

  if (tipStatus === DealLensStatus.ACCEPTED) {
    return resolveAcceptedSummaryRow(tip, deals) ?? tip ?? selectedDeal;
  }

  if (
    chatClosed &&
    (tipStatus === DealLensStatus.WITHDRAWN || CHAT_CLOSED_STATUSES.has(tipStatus))
  ) {
    return resolveDisplayOffer(deals) ?? tip ?? selectedDeal;
  }

  const selectedRow = selectedDeal?._raw ?? selectedDeal;
  const selectedStatus = normalizeDealLensStatus(selectedRow?.status);
  if (selectedStatus === DealLensStatus.ACCEPTED) {
    return resolveAcceptedSummaryRow(selectedRow, deals) ?? selectedDeal;
  }

  const scoped = Array.isArray(listScopedDeals) ? listScopedDeals : [];
  return resolveDisplayOffer(scoped) ?? selectedDeal;
}

export const OFFER_EDIT_STALE_MESSAGE =
  "This offer changed while you were editing. Please review the latest offer and try again.";

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeDeallensId(value) {
  return String(value ?? "")
    .trim()
    .replace(/^deallens#/i, "");
}

/**
 * @param {object[]} deals
 * @param {string} targetId
 * @returns {object | null}
 */
function findDealById(deals, targetId) {
  const normalizedTargetId = normalizeDeallensId(targetId);
  if (!normalizedTargetId) return null;
  for (const row of normalizeDealList(deals)) {
    const raw = asDeal(row);
    const rowId = normalizeDeallensId(raw?.deallens_id ?? raw?.id);
    if (rowId === normalizedTargetId) return raw;
  }
  return null;
}

/**
 * Resolve the live offer row for an in-progress edit at confirm time.
 *
 * @param {object} params
 * @param {string} params.targetDealId - Snapshotted deal id when edit opened
 * @param {string|null} [params.targetOfferId] - Snapshotted unique offer-row id
 * @param {object[]} [params.collectionDeals=[]]
 * @param {object[]} [params.threadDeals=[]]
 * @param {object} [params.viewerContext={}]
 * @returns {{ ok: true, targetDeal: object } | { ok: false, message: string }}
 */
export function resolveLiveOfferEditTarget({
  targetDealId,
  targetOfferId = null,
  collectionDeals = [],
  threadDeals = [],
  viewerContext = {},
}) {
  const normalizedTargetId = normalizeDeallensId(targetDealId);
  if (!normalizedTargetId) {
    return {
      ok: false,
      message: "Could not identify this offer to update. Please refresh and try again.",
    };
  }

  const aliveRow = resolveLastAliveOffer(collectionDeals);
  const aliveDeal = asDeal(aliveRow);
  const aliveId = normalizeDeallensId(aliveDeal?.deallens_id ?? aliveDeal?.id);
  if (!aliveDeal || !aliveId || aliveId !== normalizedTargetId) {
    return { ok: false, message: OFFER_EDIT_STALE_MESSAGE };
  }
  const expectedOfferId = String(targetOfferId ?? "").trim();
  const aliveOfferId = String(aliveDeal?.offer_id ?? "").trim();
  if (expectedOfferId && (!aliveOfferId || aliveOfferId !== expectedOfferId)) {
    return { ok: false, message: OFFER_EDIT_STALE_MESSAGE };
  }

  const liveDeal = aliveDeal;

  if (!canEditSentOffer(liveDeal, { ...viewerContext, activeDeal: aliveDeal })) {
    return { ok: false, message: OFFER_EDIT_STALE_MESSAGE };
  }

  return { ok: true, targetDeal: liveDeal };
}

/**
 * Prefer live messages-modal thread data, then a snapshot taken when edit opened,
 * then page-level deals. Edit confirm closes the messages modal, which clears
 * live modal thread state; the snapshot keeps the collection identity intact.
 *
 * @param {object} params
 * @param {string} params.targetDealId
 * @param {object[]} [params.messagesCollectionDeals]
 * @param {object[]} [params.messagesThreadDeals]
 * @param {object[]} [params.snapshotCollectionDeals]
 * @param {object[]} [params.snapshotThreadDeals]
 * @param {object[]} [params.pageThreadDeals]
 * @param {object | null} [params.pageSeedDeal]
 * @returns {{ collectionDeals: object[], threadDeals: object[] }}
 */
export function resolveOfferEditCollectionScope({
  targetDealId,
  messagesCollectionDeals = [],
  messagesThreadDeals = [],
  snapshotCollectionDeals = [],
  snapshotThreadDeals = [],
  pageThreadDeals = [],
  pageSeedDeal = null,
}) {
  if (Array.isArray(messagesThreadDeals) && messagesThreadDeals.length > 0) {
    return {
      collectionDeals:
        Array.isArray(messagesCollectionDeals) && messagesCollectionDeals.length > 0
          ? messagesCollectionDeals
          : messagesThreadDeals,
      threadDeals: messagesThreadDeals,
    };
  }

  if (Array.isArray(snapshotThreadDeals) && snapshotThreadDeals.length > 0) {
    return {
      collectionDeals:
        Array.isArray(snapshotCollectionDeals) && snapshotCollectionDeals.length > 0
          ? snapshotCollectionDeals
          : snapshotThreadDeals,
      threadDeals: snapshotThreadDeals,
    };
  }

  const seed = findDealById(pageThreadDeals, targetDealId) ?? pageSeedDeal ?? null;
  return {
    collectionDeals: seed ? resolveCollectionDeals(seed, pageThreadDeals) : pageThreadDeals,
    threadDeals: pageThreadDeals,
  };
}

/**
 * Finds the latest investor-authored offer before the offer being edited.
 *
 * @param {object | null | undefined} targetDeal
 * @param {object[]} deals
 * @returns {object | null}
 */
export function resolvePriorInvestorOffer(targetDeal, deals = []) {
  const target = asDeal(targetDeal);
  const targetId = normalizeDeallensId(target?.deallens_id ?? target?.id);
  const targetOfferId = String(target?.offer_id ?? "").trim();
  const targetTime = insertTime(target);

  let investorOffers = normalizeDealList(deals)
    .map((row) => asDeal(row))
    .filter((row) => {
      const rowId = normalizeDeallensId(row?.deallens_id ?? row?.id);
      const rowOfferId = String(row?.offer_id ?? "").trim();
      if (targetOfferId ? rowOfferId === targetOfferId : targetId && rowId === targetId)
        return false;
      const role = String(
        row?.sender_role ?? row?.role ?? row?.raw?.sender_role ?? row?.raw?.role ?? ""
      )
        .trim()
        .toLowerCase();
      if (role !== "investor") return false;
      const rowTime = insertTime(row);
      return !targetTime || !rowTime || rowTime < targetTime;
    })
    .sort((a, b) => insertTime(a) - insertTime(b));
  const offerRows = investorOffers.filter(
    (row) =>
      String(row?.type ?? "")
        .trim()
        .toUpperCase() === "OFFER"
  );
  if (offerRows.length > 0) investorOffers = offerRows;

  return investorOffers.at(-1) ?? null;
}

export const COLLECTION_CHAT_CLOSED_STATUSES = CHAT_CLOSED_STATUSES;
export const COLLECTION_ALIVE_STATUSES = ALIVE_STATUSES;
